<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in-up">

    <!-- Top Action Bar -->
    <div class="w-full flex justify-between items-center mb-6">
        <app-btn-back routeTo="/groups"></app-btn-back>
        <div class="flex flex-row gap-3">
            <app-btn-action label="Refresh" (clicked)="refresh()" bgColor="bg-primary-500" textColor="text-white"
                icon="fa fa-refresh"></app-btn-action>
            <app-btn-route btnText="Edit Profile" icon="fa fa-edit" routeTo="/profile/edit" bgColor="bg-primary-500"
                textColor="text-white"></app-btn-route>
        </div>
    </div>

    <!-- Redesigned Two-Column Member Details Layout -->
    <div class="flex flex-col lg:flex-row gap-6">

        <!-- Left Column: Avatar & Profile Details (flex-4) -->
        <div class="w-full lg:flex-[4]" style="flex: 4 0 0%;">
            <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col items-center">

                <!-- Hidden file input for uploading profile image -->
                <input hidden type="file" #profileImage (change)="uploadFile($event)">

                <!-- Circular Avatar Container with Upload & Delete Controls -->
                <div class="relative">
                    @if (user?.photoURL) {
                    <div class="relative rounded-full">
                        <img [src]="user?.photoURL"
                            class="h-40 w-40 bg-white object-cover rounded-full shadow-md border border-gray-100" />
                        <button (click)="profileImage.click()"
                            title="Change photo"
                            class="absolute h-10 w-10 bottom-1 right-1 bg-white text-primary-500 hover:text-white hover:bg-primary-500 text-xs uppercase font-bold flex items-center justify-center rounded-full shadow border border-gray-100 transition-all duration-150 cursor-pointer"
                            type="button">
                            <i class="fa fa-edit"></i>
                        </button>
                        <button (click)="deleteProfileImage()"
                            title="Delete photo"
                            class="absolute h-10 w-10 bottom-11 -right-3 bg-white text-red-500 hover:bg-red-500 hover:text-white text-xs uppercase font-bold flex items-center justify-center rounded-full shadow border border-gray-100 transition-all duration-150 cursor-pointer"
                            type="button">
                            <i class="fa fa-trash-can"></i>
                        </button>
                    </div>
                    } @else {
                    <div
                        class="relative bg-primary-100 uppercase text-primary-500 text-5xl flex justify-center items-center text-center h-40 w-40 rounded-full shadow-md border border-gray-100 font-semibold">
                        {{ user?.name | initials }}
                        <button type="button" (click)="profileImage.click()"
                            title="Upload photo"
                            class="absolute h-10 w-10 bottom-1 right-1 bg-white text-primary-500 hover:bg-primary-500 hover:text-white text-xs uppercase font-bold flex items-center justify-center rounded-full shadow border border-gray-100 transition-all duration-150 cursor-pointer">
                            <i class="fa fa-edit"></i>
                        </button>
                    </div>
                    }

                    @if (progress != null) {
                    <p class="absolute bg-primary-500 text-white text-xs top-1/2 left-0 right-0 text-center z-10 py-1 px-2 rounded-full shadow">
                        {{progress}}
                    </p>
                    }
                </div>

                <!-- User Full Name -->
                <h3 class="text-2xl font-bold text-gray-900 mt-5 text-center">
                    {{ user?.name || 'My Profile' }}
                </h3>

                <!-- Divider -->
                <div class="w-full border-t border-gray-100 my-6"></div>

                <!-- Info List: Reusable spacing & typography -->
                <div class="w-full space-y-5">
                    <div>
                        <span class="block text-xs font-bold text-gray-400 uppercase tracking-wider">First name</span>
                        <span class="block text-base font-semibold text-gray-800 mt-1">{{ firstName || '—' }}</span>
                    </div>

                    <div>
                        <span class="block text-xs font-bold text-gray-400 uppercase tracking-wider">Last name</span>
                        <span class="block text-base font-semibold text-gray-800 mt-1">{{ lastName || '—' }}</span>
                    </div>

                    <div>
                        <span class="block text-xs font-bold text-gray-400 uppercase tracking-wider">Mobile number</span>
                        <span class="block text-base font-semibold text-gray-800 mt-1">
                            @if (user?.contact?.contact) {
                            {{ user?.contact?.phoneCode ? '+' + user?.contact?.phoneCode : '' }} {{ user?.contact?.contact }}
                            } @else {
                            —
                            }
                        </span>
                    </div>

                    <div>
                        <span class="block text-xs font-bold text-gray-400 uppercase tracking-wider">Email address</span>
                        <span class="block text-base font-semibold text-gray-800 mt-1 break-all">{{ user?.email || '—' }}</span>
                    </div>

                    <div>
                        <span class="block text-xs font-bold text-gray-400 uppercase tracking-wider">Date of birth</span>
                        <div class="flex items-center gap-2 mt-1">
                            <span class="text-base font-semibold text-gray-800">
                                @if (user?.dob) {
                                {{ user!.dob! | moment: 'D MMM YYYY' }}
                                } @else {
                                —
                                }
                            </span>
                            @if (user?.dob) {
                            <span class="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-semibold uppercase">
                                {{ user?.isDobViewable !== false ? 'Public' : 'Private' }}
                            </span>
                            }
                        </div>
                    </div>

                    @if (user?.gender) {
                    <div>
                        <span class="block text-xs font-bold text-gray-400 uppercase tracking-wider">Gender</span>
                        <span class="block text-base font-semibold text-gray-800 mt-1 capitalize">{{ user?.gender }}</span>
                    </div>
                    }

                    <div>
                        <span class="block text-xs font-bold text-gray-400 uppercase tracking-wider">Profession</span>
                        <span class="block text-base font-semibold text-gray-800 mt-1 capitalize">{{ user?.profession || '—' }}</span>
                    </div>
                </div>

            </div>
        </div>

        <!-- Right Column: Addresses & Location Map (flex-8) -->
        <div class="w-full lg:flex-[8] flex flex-col gap-6" style="flex: 8 0 0%;">

            <!-- Addresses Card -->
            <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h3 class="text-lg font-bold text-gray-900 mb-5">
                    My addresses
                </h3>

                @if (user?.address && (user?.address?.addressLine1 || user?.address?.city || user?.address?.buildingName)) {
                <!-- Clean address card matching member detail design -->
                <div class="bg-gray-50 rounded-xl p-5 border border-gray-100 relative mb-6">
                    <div class="flex items-start justify-between">
                        <div>
                            <span class="block font-semibold text-gray-800 text-sm">Home Address</span>
                            <span class="block text-sm text-gray-600 mt-2">
                                {{ user?.address?.buildingNo ? user?.address?.buildingNo + ' ' : '' }}
                                {{ user?.address?.buildingName ? user?.address?.buildingName + ', ' : '' }}
                                {{ user?.address?.addressLine1 }}
                                {{ user?.address?.addressLine2 ? ', ' + user?.address?.addressLine2 : '' }}
                            </span>
                            <span class="block text-sm text-gray-500 mt-1">
                                {{ user?.address?.city }}{{ user?.address?.country ? ', ' + user?.address?.country : '' }}
                            </span>
                        </div>
                    </div>
                </div>
                } @else {
                <div class="text-sm text-gray-400 py-6 italic">
                    No address information provided yet. Click "Edit Profile" to add your address.
                </div>
                }

                <!-- Map (shown only if location is provided by user) -->
                @if (user?.location?.position) {
                <div class="border-t border-gray-100 pt-6">
                    <span class="block uppercase text-gray-400 text-xs font-bold mb-4 tracking-wider">
                        Location Map
                    </span>
                    <div class="rounded-xl overflow-hidden shadow-inner border border-gray-100">
                        <app-view-location [location]="user?.location"></app-view-location>
                    </div>
                </div>
                }
            </div>

        </div>
    </div>
</div>